import React, { useEffect, useState } from 'react';
import { Plug, Save, CheckCircle2, AlertTriangle, Loader2, Mail, MessageSquare, Share2 } from 'lucide-react';
import { suiteAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Panel, Card, Field, inputCls } from './shared';
import { toast } from 'sonner';

export default function IntegrationsPanel({ isSuperadmin }) {
  const [state, setState] = useState(null);
  const [form, setForm] = useState({ resend_api_key: '', from_email: '', from_name: '', sw_project_id: '', sw_api_token: '', sw_space_host: '', sw_from_number: '' });
  const [busy, setBusy] = useState('');
  const [accounts, setAccounts] = useState(null);
  const [social, setSocial] = useState({
    meta_page_id: '', meta_page_token: '', ig_user_id: '', ig_token: '',
    li_token: '', li_org_urn: '', li_person_urn: '',
    x_consumer_key: '', x_consumer_secret: '', x_access_token: '', x_access_secret: '',
  });

  const loadAccounts = () => suiteAPI.socialAccounts().then(r => {
    setAccounts(r.data);
    setSocial(s => ({ ...s, meta_page_id: r.data.facebook.page_id || '', ig_user_id: r.data.instagram.ig_user_id || '',
      li_org_urn: r.data.linkedin.org_urn || '', li_person_urn: r.data.linkedin.person_urn || '' }));
  });

  const saveSocial = async () => {
    setBusy('social');
    try {
      await suiteAPI.saveSocialAccounts(Object.fromEntries(Object.entries(social).filter(([, v]) => v)));
      toast.success('Social accounts saved — scheduled posts will publish automatically');
      setSocial(s => ({ ...s, meta_page_token: '', ig_token: '', li_token: '', x_consumer_secret: '', x_access_secret: '' }));
      loadAccounts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setBusy(''); }
  };

  const load = () => suiteAPI.getIntegrations().then(r => {
    setState(r.data);
    setForm(f => ({ ...f, from_email: r.data.email.from_email || '', from_name: r.data.email.from_name || '', sw_space_host: r.data.sms.space_host || '', sw_from_number: r.data.sms.from_number || '' }));
  });
  useEffect(() => { load(); loadAccounts(); }, []);

  const save = async (keys) => {
    setBusy('save');
    try {
      const payload = Object.fromEntries(keys.map(k => [k, form[k]]).filter(([, v]) => v));
      await suiteAPI.updateIntegrations(payload);
      toast.success('Saved — takes effect immediately');
      setForm(f => ({ ...f, resend_api_key: '', sw_api_token: '' }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setBusy(''); }
  };

  const Status = ({ ok, label }) => (
    <span className={`text-[11px] px-2 py-0.5 rounded-full flex items-center gap-1 ${ok ? 'bg-[#b8ff00]/15 text-[#b8ff00]' : 'bg-amber-500/15 text-amber-400'}`}>
      {ok ? <CheckCircle2 size={11} /> : <AlertTriangle size={11} />} {label}
    </span>
  );

  return (
    <Panel title="Integrations" subtitle="Connect the services that actually deliver your marketing. Everything is built already — adding keys just turns delivery on." testId="suite-integrations">
      {!isSuperadmin && <Card className="p-4 mb-4"><p className="text-xs text-amber-400">Only a superadmin can change these credentials.</p></Card>}
      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-[#b8ff00]" />
            <p className="text-sm font-bold text-white">Email — Resend</p>
            {state && <Status ok={state.email.configured} label={state.email.configured ? `connected (${state.email.source})` : 'not connected'} />}
          </div>
          <p className="text-[11px] text-zinc-500">Get a key at resend.com → API Keys. Verify your sending domain to send from your own address.</p>
          <Field label="Resend API key" hint="Stored server-side and never shown again.">
            <Input type="password" value={form.resend_api_key} onChange={e => setForm({ ...form, resend_api_key: e.target.value })} placeholder="re_..." className={inputCls} disabled={!isSuperadmin} data-testid="int-resend-key" />
          </Field>
          <Field label="From email"><Input value={form.from_email} onChange={e => setForm({ ...form, from_email: e.target.value })} placeholder="marketing@yourdomain.com" className={inputCls} disabled={!isSuperadmin} data-testid="int-from-email" /></Field>
          <Field label="From name"><Input value={form.from_name} onChange={e => setForm({ ...form, from_name: e.target.value })} placeholder="M Marketing" className={inputCls} disabled={!isSuperadmin} /></Field>
          <Button onClick={() => save(['resend_api_key', 'from_email', 'from_name'])} disabled={!isSuperadmin || busy === 'save'} className="bg-[#b8ff00] text-black font-bold" data-testid="int-save-email">
            {busy === 'save' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Save size={14} className="mr-1.5" />} Save email settings
          </Button>
        </Card>

        <Card className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <MessageSquare size={16} className="text-cyan-400" />
            <p className="text-sm font-bold text-white">SMS — SignalWire</p>
            {state && <Status ok={state.sms.configured} label={state.sms.configured ? 'connected' : 'not connected'} />}
          </div>
          <p className="text-[11px] text-zinc-500">From your SignalWire Space → API: copy the Project ID, create a token with the Messaging scope, and use a messaging-enabled number.</p>
          <Field label="Project ID"><Input value={form.sw_project_id} onChange={e => setForm({ ...form, sw_project_id: e.target.value })} className={inputCls} disabled={!isSuperadmin} data-testid="int-sw-project" /></Field>
          <Field label="API token"><Input type="password" value={form.sw_api_token} onChange={e => setForm({ ...form, sw_api_token: e.target.value })} placeholder="PT..." className={inputCls} disabled={!isSuperadmin} data-testid="int-sw-token" /></Field>
          <Field label="Space URL" hint="e.g. yourspace.signalwire.com"><Input value={form.sw_space_host} onChange={e => setForm({ ...form, sw_space_host: e.target.value })} className={inputCls} disabled={!isSuperadmin} data-testid="int-sw-host" /></Field>
          <Field label="Sending number" hint="E.164 format, e.g. +15551234567"><Input value={form.sw_from_number} onChange={e => setForm({ ...form, sw_from_number: e.target.value })} className={inputCls} disabled={!isSuperadmin} data-testid="int-sw-number" /></Field>
          <Button onClick={() => save(['sw_project_id', 'sw_api_token', 'sw_space_host', 'sw_from_number'])} disabled={!isSuperadmin || busy === 'save'} className="bg-cyan-500 text-black font-bold" data-testid="int-save-sms">
            <Save size={14} className="mr-1.5" /> Save SMS settings
          </Button>
        </Card>

        <Card className="p-5 lg:col-span-2 space-y-3">
          <div className="flex items-center gap-2">
            <Share2 size={16} className="text-cyan-400" />
            <p className="text-sm font-bold text-white">Social auto-publishing</p>
            {accounts && (
              <div className="flex gap-1.5 flex-wrap ml-2">
                {Object.entries(accounts).map(([k, v]) => (
                  <span key={k} className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${v.configured ? 'bg-[#b8ff00]/15 text-[#b8ff00]' : 'bg-zinc-800 text-zinc-500'}`}>{k}</span>
                ))}
              </div>
            )}
          </div>
          <p className="text-[11px] text-zinc-500">
            Once connected, scheduled posts publish themselves — no copy-paste. Facebook Page needs <span className="text-zinc-300">pages_manage_posts</span>; Instagram needs a Professional account linked to that Page plus <span className="text-zinc-300">instagram_content_publish</span>; LinkedIn needs <span className="text-zinc-300">w_organization_social</span> (or w_member_social) and the author URN; X needs OAuth 1.0a consumer + access keys.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <p className="text-[11px] uppercase tracking-wider text-zinc-500">Meta (Facebook + Instagram)</p>
              <Field label="Facebook Page ID"><Input value={social.meta_page_id} onChange={e => setSocial({ ...social, meta_page_id: e.target.value })} className={inputCls} disabled={!isSuperadmin} data-testid="int-fb-page" /></Field>
              <Field label="Page access token"><Input type="password" value={social.meta_page_token} onChange={e => setSocial({ ...social, meta_page_token: e.target.value })} className={inputCls} disabled={!isSuperadmin} data-testid="int-fb-token" /></Field>
              <Field label="Instagram Professional user ID"><Input value={social.ig_user_id} onChange={e => setSocial({ ...social, ig_user_id: e.target.value })} className={inputCls} disabled={!isSuperadmin} data-testid="int-ig-id" /></Field>
              <Field label="Instagram access token"><Input type="password" value={social.ig_token} onChange={e => setSocial({ ...social, ig_token: e.target.value })} className={inputCls} disabled={!isSuperadmin} /></Field>
            </div>
            <div className="space-y-2">
              <p className="text-[11px] uppercase tracking-wider text-zinc-500">LinkedIn & X</p>
              <Field label="LinkedIn access token"><Input type="password" value={social.li_token} onChange={e => setSocial({ ...social, li_token: e.target.value })} className={inputCls} disabled={!isSuperadmin} data-testid="int-li-token" /></Field>
              <Field label="LinkedIn organization URN" hint="urn:li:organization:123 (or leave blank to post as a person)"><Input value={social.li_org_urn} onChange={e => setSocial({ ...social, li_org_urn: e.target.value })} className={inputCls} disabled={!isSuperadmin} /></Field>
              <Field label="LinkedIn person URN"><Input value={social.li_person_urn} onChange={e => setSocial({ ...social, li_person_urn: e.target.value })} className={inputCls} disabled={!isSuperadmin} /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="X consumer key"><Input value={social.x_consumer_key} onChange={e => setSocial({ ...social, x_consumer_key: e.target.value })} className={inputCls} disabled={!isSuperadmin} /></Field>
                <Field label="X consumer secret"><Input type="password" value={social.x_consumer_secret} onChange={e => setSocial({ ...social, x_consumer_secret: e.target.value })} className={inputCls} disabled={!isSuperadmin} /></Field>
                <Field label="X access token"><Input value={social.x_access_token} onChange={e => setSocial({ ...social, x_access_token: e.target.value })} className={inputCls} disabled={!isSuperadmin} /></Field>
                <Field label="X access secret"><Input type="password" value={social.x_access_secret} onChange={e => setSocial({ ...social, x_access_secret: e.target.value })} className={inputCls} disabled={!isSuperadmin} /></Field>
              </div>
            </div>
          </div>
          <Button onClick={saveSocial} disabled={!isSuperadmin || busy === 'social'} className="bg-violet-500 text-white" data-testid="int-save-social">
            {busy === 'social' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Save size={14} className="mr-1.5" />} Save social accounts
          </Button>
        </Card>

        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-2">
            <Plug size={16} className="text-violet-400" />
            <p className="text-sm font-bold text-white">Already wired in</p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ['Sora 2 video ads', 'Generating video ads from a written brief'],
              ['AI copy & strategy', 'Campaign plans, copy, briefs, personas, insights'],
              ['Gemini image creative', 'Ad creatives and backgrounds in the design studio'],
              ['Object storage', 'Assets, designs and imports served for pages and emails'],
            ].map(([t, d]) => (
              <div key={t} className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
                <p className="text-xs font-semibold text-white">{t}</p>
                <p className="text-[11px] text-zinc-500">{d}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </Panel>
  );
}
